/**
 * Confirm + finalise dialog for `agreed` records (B19). Opened from the
 * review queue when the user clicks an `agreed` row. Modal pattern
 * mirrors `RollbackConfirmDialog` / `DeleteCollectionModal`.
 *
 * Cascade (all gated on `open` so closing mid-fetch doesn't keep
 * requests alive):
 *   - `useDatasetRecord(recordId, { enabled: open })` — record detail
 *     (first + second annotation labels).
 *   - `useCollection(collectionId)` — taxonomy key.
 *   - `useVersionDetail(taxonomyKey, versionId)` — taxonomy dimensions
 *     for the ComparisonPane.
 *   - `useUsersById([first.annotatorId, second.annotatorId])` — names
 *     for the column headers (B15-FE).
 *   - `useFinaliseRecord(recordId)` — POST + invalidations.
 *
 * Layout: title + description, then EITHER skeleton (loading) OR error
 * panel (with retry) OR read-only ComparisonPane. Footer is always
 * Cancel + primary "Finalise & add to golden dataset"; primary disables
 * while pending OR while the cascade is still loading OR errored.
 *
 * Toasts:
 *   - success: `Record finalised as agreed` (closes the dialog)
 *   - error:   `err.message ?? 'Failed to finalise record.'` (stays open)
 *
 * `ComparisonPane` is reused as-is. With all dimensions pre-expanded and
 * `onToggleExpanded` wired to a no-op the pane renders read-only enough.
 * A real `readOnly?: boolean` prop is a trivial follow-up if the
 * dimension-header click affordance becomes a real UX issue.
 */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';
import { useCollection } from '../../../datasets/hooks/useDatasets';
import { useVersionDetail } from '../../../hooks/useTaxonomy';
import { useDatasetRecord, useFinaliseRecord } from '../../hooks/useAnnotation';
import { useUsersById } from '../../hooks/useUsers';
import ComparisonPane from './ComparisonPane';

interface Props {
  open: boolean;
  /** Null when the dialog is closed; non-null record id when open. */
  recordId: string | null;
  onOpenChange: (open: boolean) => void;
}

const FinaliseAgreedDialog: React.FC<Props> = ({
  open,
  recordId,
  onOpenChange,
}) => {
  const recordQuery = useDatasetRecord(recordId ?? '', {
    enabled: open && recordId !== null,
  });

  const annotatorIds = useMemo(() => {
    const ids: string[] = [];
    const a = recordQuery.data?.firstAnnotation?.annotatorId;
    const b = recordQuery.data?.secondAnnotation?.annotatorId;
    if (a) ids.push(a);
    if (b && b !== a) ids.push(b);
    return ids;
  }, [
    recordQuery.data?.firstAnnotation?.annotatorId,
    recordQuery.data?.secondAnnotation?.annotatorId,
  ]);
  const usersQuery = useUsersById(annotatorIds);

  const collectionId = recordQuery.data?.record.collectionId;
  const collectionQuery = useCollection(collectionId ?? '');

  const taxonomyKey = collectionQuery.data?.taxonomyKey ?? '';
  const versionId = recordQuery.data?.record.taxonomyVersionId ?? '';
  const versionQuery = useVersionDetail(taxonomyKey, versionId);

  const dimensions = useMemo(
    () => versionQuery.data?.taxonomyData?.dimensions ?? [],
    [versionQuery.data?.taxonomyData?.dimensions],
  );

  // Agreed annotations match on every dimension — pre-expand so the user
  // sees everything they're promoting at a glance, no clicking required.
  const expandedDimensions = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const dim of dimensions) out[dim.id] = true;
    return out;
  }, [dimensions]);

  const finalise = useFinaliseRecord(recordId ?? '');
  const [retryNonce, setRetryNonce] = useState(0);

  const cascadeLoading =
    recordQuery.isLoading ||
    (collectionId !== undefined && collectionQuery.isLoading) ||
    (taxonomyKey !== '' && versionId !== '' && versionQuery.isLoading);
  const cascadeError =
    recordQuery.isError || collectionQuery.isError || versionQuery.isError;

  const handleFinalise = () => {
    if (!recordId) return;
    finalise.mutate(undefined, {
      onSuccess: () => {
        toast.success('Record finalised as agreed');
        onOpenChange(false);
      },
      onError: (err: Error) => {
        toast.error(err.message || 'Failed to finalise record.');
      },
    });
  };

  const handleRetryDetail = () => {
    setRetryNonce((n) => n + 1);
    void recordQuery.refetch();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="finalise-agreed-dialog"
      >
        <DialogHeader>
          <DialogTitle>Finalise as agreed?</DialogTitle>
          <DialogDescription>
            Both annotators selected the same labels on every dimension.
            Finalising promotes the record to the golden dataset.
          </DialogDescription>
        </DialogHeader>

        {cascadeLoading ? (
          <div className="space-y-2" data-testid="finalise-agreed-loading">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-10 animate-pulse rounded-md border border-border/70 bg-muted"
              />
            ))}
          </div>
        ) : cascadeError || !recordQuery.data ? (
          <div
            className="flex flex-col items-center gap-2 py-6 text-center text-[13px] text-muted-foreground"
            data-testid="finalise-agreed-error"
          >
            <p>We couldn&apos;t load this record.</p>
            <Button
              variant="link"
              size="sm"
              onClick={handleRetryDetail}
              data-testid="finalise-agreed-retry"
            >
              Retry
            </Button>
            <span className="sr-only">{retryNonce}</span>
          </div>
        ) : (
          <div
            className="max-h-[55vh] overflow-y-auto"
            data-testid="finalise-agreed-comparison"
          >
            <ComparisonPane
              dimensions={dimensions}
              firstAnnotation={recordQuery.data.firstAnnotation}
              secondAnnotation={recordQuery.data.secondAnnotation}
              expandedDimensions={expandedDimensions}
              onToggleExpanded={() => {
                /* read-only — toggling is suppressed in this surface */
              }}
              namesByAnnotatorId={usersQuery.data}
            />
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={finalise.isPending}
            data-testid="finalise-agreed-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleFinalise}
            disabled={
              finalise.isPending || cascadeLoading || cascadeError || !recordId
            }
            data-testid="finalise-agreed-confirm"
          >
            {finalise.isPending
              ? 'Finalising…'
              : 'Finalise & add to golden dataset'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FinaliseAgreedDialog;
